// ============================================================================
// Subida de la caja de contingencia (modo contingencia, paso 3d · oct-2026)
//
// Cuando vuelve la conexión, lo que se hizo en la caja sin internet se manda
// al servidor en el orden en que pasó, con la llave (Idempotency-Key) y la
// hora original (X-Hecho-En) de cada cambio.
//
// Reglas:
//   - Antes de mandar, las líneas quedan congeladas (ver congelar): un
//     reintento manda siempre lo mismo con la misma llave.
//   - El orden es el de las horas originales, mezclando pedidos, sin romper
//     el orden propio de cada pedido (crear, agregar, facturar, pagar). Así
//     los folios salen en el orden en que se cobró.
//   - Sin red (o 502/503/504): se para todo y se reintenta después. Nada se
//     marca como fallido por eso.
//   - Sesión vencida (401): se para todo. La cola sigue en el PC y se sube al
//     volver a entrar.
//   - Cualquier otra respuesta de error: ESE pedido se frena en "Requiere
//     revisión" con lo que dijo el servidor, y los demás siguen.
//   - Nada se borra del PC: lo subido queda marcado como 'subido'.
//
// Sin React, fetch ni IndexedDB: se prueba con node (subida-contingencia.test.ts).
// ============================================================================
import {
  necesitaSubida, opsPendientes, congelar, registrarHecha, marcarRevision, cerrarSiCompleto,
  type PedidoContingencia, type OperacionSubida,
} from './caja-contingencia.ts';
import { esFallaDeRed } from './conexion.ts';

export type TipoFalla = 'red' | 'sesion' | 'rechazo';

/** Qué hacer con un error al mandar una operación. */
export function tipoDeFalla(e: unknown): TipoFalla {
  const status = (e as { status?: unknown } | null)?.status;
  // Sin status no es una respuesta del servidor ni una falla de red conocida:
  // mejor frenar ese pedido que reintentar para siempre.
  if (typeof status !== 'number') return 'rechazo';
  if (esFallaDeRed(status)) return 'red';
  if (status === 401) return 'sesion';
  return 'rechazo';
}

export interface PasoSubida {
  pedidoId: string;
  op: OperacionSubida;
}

const tiempo = (iso: string) => {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
};

/**
 * Todas las operaciones pendientes, en el orden en que pasaron. Mezcla los
 * pedidos por hora pero cada pedido conserva su orden (no se puede agregar a
 * un pedido antes de crearlo, aunque el reloj del PC diga otra cosa).
 */
export function planDeSubida(pedidos: PedidoContingencia[]): PasoSubida[] {
  const colas = pedidos
    .filter(necesitaSubida)
    .sort((a, b) => tiempo(a.creadoEn) - tiempo(b.creadoEn) || a.id.localeCompare(b.id))
    .map(p => ({ id: p.id, ops: opsPendientes(p) }))
    .filter(c => c.ops.length > 0);
  const plan: PasoSubida[] = [];
  for (;;) {
    let mejor = -1;
    for (let i = 0; i < colas.length; i++) {
      if (!colas[i].ops.length) continue;
      if (mejor < 0 || tiempo(colas[i].ops[0].hechoEn) < tiempo(colas[mejor].ops[0].hechoEn)) mejor = i;
    }
    if (mejor < 0) return plan;
    plan.push({ pedidoId: colas[mejor].id, op: colas[mejor].ops.shift()! });
  }
}

export interface DepsSubida {
  /** Todos los pedidos de contingencia de este equipo, en su última versión. */
  listar(): PedidoContingencia[];
  /** Cambia un pedido en el almacén local (lee, aplica y escribe de una vez) y devuelve cómo quedó. */
  actualizar(id: string, fn: (p: PedidoContingencia) => PedidoContingencia): Promise<PedidoContingencia>;
  /** Manda una operación (con la ruta ya completa). Lanza un error con `status` si falla. */
  enviar(op: OperacionSubida): Promise<unknown>;
  ahora(): string;
}

export interface ResultadoSubida {
  /** Operaciones que el servidor confirmó en esta vuelta. */
  confirmadas: number;
  /** Pedidos que quedaron completos en el servidor en esta vuelta. */
  subidos: number;
  /** Pedidos que se frenaron en "Requiere revisión" en esta vuelta. */
  revision: number;
  /** Si se paró antes de terminar: sin red o sesión vencida. */
  detenida: null | 'red' | 'sesion';
  /** Pedidos que todavía tienen algo para subir (por ejemplo, un plato agregado mientras subía). */
  quedan: number;
}

const mensajeDe = (e: unknown) => {
  const m = (e as { message?: unknown } | null)?.message;
  return typeof m === 'string' && m.trim() ? m.trim() : 'El servidor rechazó el cambio.';
};

/** Una vuelta de subida. No se llama dos veces a la vez (lo cuida quien la usa). */
export async function subirPendientes(d: DepsSubida): Promise<ResultadoSubida> {
  const r: ResultadoSubida = { confirmadas: 0, subidos: 0, revision: 0, detenida: null, quedan: 0 };
  const candidatos = d.listar().filter(necesitaSubida).map(p => p.id);

  // 1. Congelar lo que se va a mandar, ANTES de mandarlo.
  const congelados: PedidoContingencia[] = [];
  for (const id of candidatos) congelados.push(await d.actualizar(id, congelar));

  // 2. Mandar en orden. Un pedido rechazado se frena; los demás siguen.
  const frenados = new Set<string>();
  const actual = (id: string) => d.listar().find(p => p.id === id);
  for (const paso of planDeSubida(congelados)) {
    if (frenados.has(paso.pedidoId)) continue;
    const p = actual(paso.pedidoId);
    if (!p || !necesitaSubida(p) || (p.subida?.hechas ?? []).includes(paso.op.llave)) continue;

    const idServidor = p.subida?.pedidoId ?? p.servidor?.pedidoId ?? null;
    if (paso.op.ruta.includes(':pedido') && !(Number(idServidor) > 0)) {
      await d.actualizar(p.id, q => marcarRevision(q, paso.op, 'No se sabe el número del pedido en el servidor.', d.ahora()));
      frenados.add(p.id);
      r.revision++;
      continue;
    }

    let respuesta: unknown;
    try {
      respuesta = await d.enviar({ ...paso.op, ruta: paso.op.ruta.replace(':pedido', String(idServidor)) });
    } catch (e) {
      const tipo = tipoDeFalla(e);
      if (tipo !== 'rechazo') {
        r.detenida = tipo;
        break;
      }
      await d.actualizar(p.id, q => marcarRevision(q, paso.op, mensajeDe(e), d.ahora()));
      frenados.add(p.id);
      r.revision++;
      continue;
    }

    if (paso.op.que === 'crear' && !(Number((respuesta as { id?: unknown } | null)?.id) > 0)) {
      await d.actualizar(p.id, q => marcarRevision(q, paso.op, 'El servidor no devolvió el número del pedido.', d.ahora()));
      frenados.add(p.id);
      r.revision++;
      continue;
    }
    await d.actualizar(p.id, q => registrarHecha(q, paso.op, respuesta));
    r.confirmadas++;
  }

  // 3. Lo que quedó completo pasa a 'subido'.
  if (!r.detenida) {
    for (const id of candidatos) {
      if (frenados.has(id)) continue;
      const antes = actual(id);
      const despues = await d.actualizar(id, q => cerrarSiCompleto(q, d.ahora()));
      if (antes?.estado !== 'subido' && despues.estado === 'subido') r.subidos++;
    }
  }
  r.quedan = d.listar().filter(necesitaSubida).length;
  return r;
}
